/* Mission panel (DESIGN-tutorial-system.md §1.1): a collapsible bottom-left
 * checklist of the current chapter's tasks, checkmarks driven by store/state
 * predicates. The spotlight teaches; this tolerates wandering — tasks check off
 * however the student gets there. After the last step it shows the series
 * finale ("next steps") until closed.
 *
 * It also gets out of the spotlight's way. Anchored bottom-left it used to sit
 * squarely on top of the strategy-journal card's "Save entry" button — the very
 * control the journal step asks the student to press, so the step could not be
 * completed at all. It now tries four resting places and takes the first that
 * clears the highlighted rect. Every candidate box is derived from the panel's
 * measured size and the viewport, never from where the panel currently is, so
 * the choice is a pure function of the hole and cannot oscillate. */
import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import type { Rect } from './Spotlight';

export interface MissionTaskView { id: string; label: string; done: boolean }

const M = 16;      // .tut-panel's left/right inset
const RESTING = 96; // .tut-panel's bottom inset — clears the sticky submit bar
const GAP = 12;    // breathing room when tucked above/below the hole

/** Where the panel should sit to stay clear of `avoid`, as an inline override
 *  of .tut-panel's resting position. */
function usePlacement(avoid: Rect | null | undefined): [CSSProperties | undefined, RefObject<HTMLDivElement>] {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties | undefined>(undefined);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!avoid) { setStyle((s) => (s ? undefined : s)); return; }
    const { width: w, height: h } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Clear the step card too — it is placed from the hole, never from the
    // panel, so reading it here cannot feed back into this decision.
    const cardR = document.querySelector('.tut-card')?.getBoundingClientRect() ?? null;
    const clearOf = (box: { left: number; top: number; width: number; height: number } | null) =>
      (left: number, top: number) =>
        !box || box.left >= left + w || box.left + box.width <= left ||
        box.top >= top + h || box.top + box.height <= top;
    const clearsHole = clearOf(avoid);
    const clearsCard = clearOf(cardR);

    // In preference order: resting spot, mirrored to the right, tucked above
    // the hole, tucked below it. A full-width anchor defeats the first two,
    // which is exactly the journal card's case.
    const candidates: Array<[CSSProperties | undefined, number, number]> = [
      [undefined, M, vh - RESTING - h],
      [{ left: 'auto', right: M }, vw - M - w, vh - RESTING - h],
      [{ bottom: vh - avoid.top + GAP }, M, avoid.top - GAP - h],
      [{ bottom: 'auto', top: avoid.top + avoid.height + GAP }, M, avoid.top + avoid.height + GAP],
      [{ left: 'auto', right: M, bottom: vh - avoid.top + GAP }, vw - M - w, avoid.top - GAP - h],
    ];
    const onScreen = ([, , t]: [unknown, number, number]) => t >= M && t + h <= vh - M;
    const usable = candidates.filter((c) => onScreen(c) && clearsHole(c[1], c[2]));
    // Prefer one that also clears the card; a spot that only clears the hole
    // still beats sitting on the control the step is pointing at.
    const fit = usable.find(([, l, t]) => clearsCard(l, t)) ?? usable[0];
    const next = fit ? fit[0] : undefined;
    setStyle((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  });
  return [style, ref];
}

export function MissionPanel({
  seriesTitle, chapterIdx, chapterCount, chapterTitle, tasks, collapsed,
  finished, finale, onToggle, onSkip, onCloseFinale, avoid,
}: {
  seriesTitle: string;
  chapterIdx: number;
  chapterCount: number;
  chapterTitle: string;
  tasks: MissionTaskView[];
  collapsed: boolean;
  finished: boolean;
  finale?: { title: string; items: string[] };
  onToggle: () => void;
  onSkip: () => void;
  onCloseFinale: () => void;
  /** The spotlight hole to stay clear of. */
  avoid?: Rect | null;
}) {
  const [style, ref] = usePlacement(avoid);
  if (finished) {
    if (!finale) return null;
    return (
      <div ref={ref} className="tut-panel p-3" style={style}>
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-bold text-txt">🏁 {finale.title}</div>
          <button className="text-xs text-dim hover:text-txt" onClick={onCloseFinale} aria-label="Close">✕</button>
        </div>
        <ul className="mt-2 space-y-1.5 text-sm text-mut">
          {finale.items.map((it) => <li key={it}>• {it}</li>)}
        </ul>
      </div>
    );
  }

  return (
    <div ref={ref} className="tut-panel" style={style}>
      <button
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <span className="text-xs font-bold uppercase tracking-wide text-acc">
          🎯 {seriesTitle} — {chapterIdx + 1}/{chapterCount}
        </span>
        <span className="text-mut">{collapsed ? '▸' : '▾'}</span>
      </button>
      {!collapsed && (
        <div className="px-3 pb-3">
          <div className="mb-1.5 text-sm font-semibold text-txt">{chapterTitle}</div>
          <ul className="space-y-1 text-sm">
            {tasks.map((t) => (
              <li key={t.id} className={'flex items-start gap-2 ' + (t.done ? 'text-good' : 'text-mut')}>
                <span aria-hidden="true">{t.done ? '✓' : '○'}</span>
                <span className={t.done ? 'line-through opacity-80' : ''}>{t.label}</span>
              </li>
            ))}
          </ul>
          <button className="mt-2 text-xs text-dim hover:text-txt" onClick={onSkip}>
            Skip tour — I’ll explore
          </button>
        </div>
      )}
    </div>
  );
}
